import React, { useContext, useEffect } from "react";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@mui/material/Dialog";
import { useQueryProduct } from "../../../queries/product/hooks/useQueryProduct";
import Divider from "@mui/material/Divider";
import { CircularProgress } from "@mui/material";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { Item } from "../Item/Item";
import { formatPrice, getSealDetailsDisplay } from "../../utils/utils";
import moment from "moment/moment";
import { UserContext } from "../../../hooks/userContext";
import Box from "@mui/material/Box";
import { grey } from "@mui/material/colors";
import axios from "axios";
import BuyAction from "../../../routes/Buy/BuyAction";

interface ActionRequiredOrdersProps {
  params: any;
  setParams: (params: any) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  handleCart: (params: any, packageAvailable?: number) => void;
  loading: boolean;
}

export const ProductDialog: React.FunctionComponent<
  ActionRequiredOrdersProps
> = ({
  params,
  setParams,
  open,
  setOpen,
  handleCart,
  loading: cartLoading,
}) => {
  const { id }: any = useContext(UserContext);
  const { loading, data } = useQueryProduct({
    variables: { where: { id: params.id } },
    fetchPolicy: "no-cache",
  });

  const [imageUrl, setImageUrl] = React.useState<string | null>(null);

  useEffect(() => {
    const ndc = data?.findUniqueProduct?.ndc || null;
    const formatedNdc = ndc?.replace(/-/g, "");
    if (!formatedNdc) return;
    const imageUrlApi = `${process.env.REACT_APP_API_URL}/drug-images?ndc=${formatedNdc}`;
    axios.get(imageUrlApi).then((response) => {
      setImageUrl(response.data.imageUrl);
    });
  }, [data]);

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={() => {
        setParams(null);
        setOpen(false);
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">Product Details</DialogTitle>
      <Divider />
      <DialogContent
        sx={{
          height: "400px",
          position: "relative",
          pb: 0,
          pl: 0,
          pr: 2,
          pt: 2,
        }}
      >
        {loading ? (
          <CircularProgress
            sx={{ position: "absolute", left: "calc(50% - 24px)", top: "50%" }}
            color="inherit"
          />
        ) : (
          <Grid container spacing={0} sx={{ height: "100%" }}>
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              sx={{
                borderRight: "1px solid rgba(0, 0, 0, 0.12);",
                pl: 2,
                pr: 2,
              }}
            >
              <Typography sx={{ mb: 1 }} component="h1" variant="h6">
                Product Description
              </Typography>
              <Item
                label="Generic name"
                value={data.findUniqueProduct.drugName}
              />
              <Item label="Strength" value={data.findUniqueProduct.strength} />
              <Item label="Form" value={data.findUniqueProduct.form} />
              <Item label="Storage" value={data.findUniqueProduct.storage} />
              <Item
                label="Labeler/Mfg"
                value={data.findUniqueProduct.labelerName}
              />
              {data.findUniqueProduct.isFrozen && (
                <Item label="Product type" value="Refrigerated" />
              )}
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              sx={{
                borderRight: "1px solid rgba(0, 0, 0, 0.12);",
                pl: 2,
                pr: 2,
              }}
            >
              <Typography sx={{ mb: 1 }} component="h1" variant="h6">
                Package Details
              </Typography>
              <Item
                label="Lot Number"
                value={data.findUniqueProduct.lotNumber}
              />
              <Item label="Packaging" value={data.findUniqueProduct.notes} />
              <Item
                label="Package Condition"
                value={
                  data.findUniqueProduct.packageCondition === 1
                    ? "Sealed"
                    : "Non-Sealed"
                }
              />
                             {data.findUniqueProduct.sealDetails && (
                 <Item
                   label="Package Condition Details"
                   value={getSealDetailsDisplay(data.findUniqueProduct.sealDetails)}
                 />
               )}
              <Item
                label="Package Type"
                value={data.findUniqueProduct.type === 1 ? "Full" : "Partial"}
              />
              {data.findUniqueProduct.type === 2 && (
                <Item
                  label="Partial quantity"
                  value={data.findUniqueProduct.packaging}
                />
              )}
              <Item
                label="Package Quantity"
                value={data.findUniqueProduct.quantityPerPack}
              />
              <Item
                label="Exp.Date"
                value={moment(data.findUniqueProduct.expDate).format("MM/YYYY")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ pl: 2 }}>
              <Typography sx={{ mb: 1 }} component="h1" variant="h6">
                Price Details
              </Typography>
              <Item
                label="Unit Price"
                value={`$${formatPrice(data.findUniqueProduct.unitPrice)}`}
              />
              {imageUrl && (
              <Grid item xs={12} sm={6} md={4} sx={{ pt: 2 }}>
                <img
                  src={imageUrl}
                  alt="Product"
                  style={{
                    width: "100%",
                    height: "auto",
                    maxHeight: "200px",
                    objectFit: "contain",
                  }}
                ></img>
              </Grid>
            )}
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2, backgroundColor: grey[100] }}>
        <Grid container sx={{ height: "100%", display: "flex" }}>
          {(data?.findUniqueProduct?.ProductPackages?.length ?? 0) > 0 && (
            <Grid item xs={12} sm={6}>
              <Typography sx={{ pl: 1 }} component="h1" variant="h6">
                Package Condition (Seller Notes)
              </Typography>
              {data?.findUniqueProduct?.ProductPackages.map((item: any) => (
                <Box key={item.Package.key} sx={{ mt: 1, pl: 1, fontSize: 12 }}>
                  {item.Package.name}
                </Box>
              ))}
            </Grid>
          )}
          <Grid
            item
            xs={12}
            sm={((data?.findUniqueProduct?.ProductPackages?.length ?? 0) === 0) ? 10 : 4}
          >
            <Typography sx={{ pl: 1 }} component="h1" variant="h6">
              Package Price
            </Typography>
            <Box sx={{ mt: 1, pl: 1, fontSize: 14 }}>{`$${formatPrice(
              data?.findUniqueProduct?.price || 0
            )}`}</Box>
          </Grid>
          {params.row.userId !== +id && data?.findUniqueProduct?.quantity > 0 && (
            <Grid item xs={12} sm={6} md={4} sx={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end" }}>
              <BuyAction
                key={params.id}
                rowParams={{
                  ...params,
                  row: data?.findUniqueProduct
                    ? { ...params.row, available: data.findUniqueProduct.quantity }
                    : params.row,
                }}
                updateCartLoading={cartLoading}
                useAddToCartButton
                handleAvailableCart={(qty) => {
                  if (data?.findUniqueProduct?.quantity > 0) {
                    handleCart(params, qty);
                  }
                }}
              />
            </Grid>
          )}
        </Grid>
      </DialogActions>
    </Dialog>
  );
};
